import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { iEmployeeWorkShiftFormSchema, tProject } from "../../cached-data/types"
import { getFlagEnabled } from "../../getFlagValue"
import * as API from "../../api"

/** One timekeeping entry within a stored TimecardTemplate's `contents.timecard.entries`. */
type iTimecardTemplateEntry = {
    employee: number
    project_cost_code: number
    adjusted_minutes_st: number
    adjusted_minutes_ot: number
    adjusted_minutes_dt: number
    comment: string
    modifiers: Record<string, number>
}

/** One shift extra within a stored TimecardTemplate's `contents.timecard.shift_extras`. */
type iTimecardTemplateShiftExtra = {
    employee: number
    schema: number
    project: number
    store: Record<string, unknown>
}

/** One absence within a stored TimecardTemplate's `contents.timecard.absences`. */
type iTimecardTemplateAbsence = {
    employee: number
    absence_type: number
    type: string
    project: number | null
    start_time: string
    end_time: string
}

/** One TimecardTemplate, as returned by GET /api/v4/timecard_templates/. */
export type iTimecardTemplate = {
    id: number
    name: string
    contents: {
        schema_version: number
        setup: {
            projects: number[]
            employees: number[]
            project_cost_codes: number[]
        }
        timecard: {
            shift_times: {
                start: string
                end: string
                end_day_offset: number
                timezone: string
            }
            /** Default foreman shown when the template is applied; the applying user can still change it. */
            foreman?: number
            entries: iTimecardTemplateEntry[]
            shift_extras: iTimecardTemplateShiftExtra[]
            absences: iTimecardTemplateAbsence[]
        }
    }
}

/** One timekeeping entry within an ApplyTemplateResponse's `payload.timekeepingEntries`. */
type iApplyPayloadTimekeepingEntry = {
    employee: number
    foreman: number
    cost_code: number
    adjusted_minutes_st: number
    adjusted_minutes_ot: number
    adjusted_minutes_dt: number
    comment: string
    modifier_active: Record<string, number> | null
    date: string
    shift_start_time: string
    shift_end_time: string
}

/** One shift extra within an ApplyTemplateResponse's `payload.employeeEntries`. */
type iApplyPayloadEmployeeEntry = {
    employee: number
    foreman: number
    schema: number
    project: number
    store: Record<string, unknown>
    date: string
    shift_start_time: string
    shift_end_time: string
}

/** One absence within an ApplyTemplateResponse's `payload.absences`. */
type iApplyPayloadAbsence = {
    employee: number
    foreman: number
    absence_type: number
    type: string
    project: number | null
    date: string
    shift_start_time: string
    shift_end_time: string
}

/** One row-level problem/warning: identifies the specific template row an availability check flagged. */
type iApplyRowDetail = { employee: number; section: string; index: number | null; reference: string; id: number | null; reason: string }

/** The response shape of POST /api/v4/timecard_templates/:id/apply/. */
export type ApplyTemplateResponse = {
    valid: boolean
    problems: iApplyRowDetail[]
    warnings: Array<{ type: "existing_work_shift"; employee: number } | ({ type: "inactive_cost_code" } & iApplyRowDetail)>
    payload: {
        timekeepingEntries: iApplyPayloadTimekeepingEntry[]
        employeeEntries: iApplyPayloadEmployeeEntry[]
        absences: iApplyPayloadAbsence[]
        projectList: number[]
        shift_start_time: string
        shift_end_time: string
    } | null
}

const startOfCalendarDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * Build the TimecardTemplateContents shape for a setup-only template (no entries/shift_extras/
 * absences), captured from the create-time-card modal's own selector state before any WorkShift
 * exists to snapshot.
 *
 * @param projects - Currently selected projects.
 * @param employees - Currently selected employees.
 * @param costCodes - Currently selected cost codes.
 * @param shiftStartTime - The modal's shift start time.
 * @param shiftEndTime - The modal's shift end time.
 * @param timezone - IANA timezone name to store the shift times against.
 * @param foreman - The modal's currently-selected foreman, stored as the template's default.
 * @returns A contents object ready to POST as the `contents` field of a template create request.
 */
export const buildSetupOnlyContents = (
    projects: Array<{ id: number }>,
    employees: Array<{ id: number }>,
    costCodes: Array<{ id: number }>,
    shiftStartTime: Date,
    shiftEndTime: Date,
    timezone: string,
    foreman: number
): iTimecardTemplate["contents"] => {
    const toHoursMinutes = (date: Date) => `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`
    const endDayOffset = Math.round((startOfCalendarDay(shiftEndTime).getTime() - startOfCalendarDay(shiftStartTime).getTime()) / (24 * 60 * 60 * 1000))

    return {
        schema_version: 1,
        setup: {
            projects: projects.map(p => p.id),
            employees: employees.map(e => e.id),
            project_cost_codes: costCodes.map(c => c.id),
        },
        timecard: {
            shift_times: {
                start: toHoursMinutes(shiftStartTime),
                end: toHoursMinutes(shiftEndTime),
                end_day_offset: endDayOffset,
                timezone,
            },
            foreman,
            entries: [],
            shift_extras: [],
            absences: [],
        },
    }
}

/** A timekeeping entry grid row built from a template apply, matching populateInitialRowData's shape. */
type iTemplateTimekeepingRow = {
    employee: number
    cost_code: number
    adjusted_minutes_st: number
    adjusted_minutes_ot: number
    adjusted_minutes_dt: number
    comment: string
    modifier_active: Record<string, number> | null
    date: string
    foreman: number
    shift_start_time: Date
    shift_end_time: Date
    work_shift_id: Record<string, unknown>
    isPlaceholder: false
}

/** An employee-entry (shift extra) grid row built from a template apply. */
type iTemplateEmployeeEntryRow = {
    employee: number
    schema: iEmployeeWorkShiftFormSchema | number
    project: tProject | number
    store: Record<string, unknown>
    date: string
    foreman: number
    shift_start_time: Date
    shift_end_time: Date
    work_shift_id: Record<string, unknown>
    isPlaceholder: false
}

/** An absence grid row built from a template apply. */
type iTemplateAbsenceRow = {
    employee: number
    absence_type: number
    type: string
    project: tProject | number | null
    date: string
    foreman: number
    shift_start_time: Date
    shift_end_time: Date
    work_shift_id: Record<string, unknown>
    isPlaceholder: false
}

/**
 * Convert an `apply` response's wire-shaped payload rows into grid rows shaped like the ones
 * `populateInitialRowData` builds in modal-create-time-card.tsx -- resolving `schema`/`project`
 * ids to the full objects the grid's column defs expect, and attaching the shared context
 * fields every row in this modal carries (foreman, work_shift_id placeholder). `isPlaceholder`
 * is always `false`: these rows carry the template's real saved data, not an empty row waiting
 * to be filled in, so they shouldn't get the grid's default placeholder treatment.
 *
 * @param applyPayload - The non-null `payload` from an ApplyTemplateResponse.
 * @param contextFields - Fields shared by every row produced in this modal session.
 * @param shiftExtraSchemas - Already-loaded shift extra schemas, for resolving `schema` ids.
 * @param projectsById - Already-loaded projects keyed by id, for resolving `project` ids.
 * @returns Row arrays ready to append into the modal's MutableSourceData.
 */
export const buildTemplateRows = (
    applyPayload: NonNullable<ApplyTemplateResponse["payload"]>,
    contextFields: { foreman: number; workShift: Record<string, unknown> },
    shiftExtraSchemas: iEmployeeWorkShiftFormSchema[],
    projectsById: Record<number, tProject>
): {
    timekeepingEntries: iTemplateTimekeepingRow[]
    employeeEntries: iTemplateEmployeeEntryRow[]
    absences: iTemplateAbsenceRow[]
} => {
    const schemasById: Record<number, iEmployeeWorkShiftFormSchema> = {}
    shiftExtraSchemas.forEach(schema => {
        schemasById[schema.id] = schema
    })

    const timekeepingEntries: iTemplateTimekeepingRow[] = applyPayload.timekeepingEntries.map(entry => ({
        employee: entry.employee,
        cost_code: entry.cost_code,
        adjusted_minutes_st: entry.adjusted_minutes_st,
        adjusted_minutes_ot: entry.adjusted_minutes_ot,
        adjusted_minutes_dt: entry.adjusted_minutes_dt,
        comment: entry.comment,
        modifier_active: entry.modifier_active,
        date: entry.date,
        foreman: contextFields.foreman,
        shift_start_time: new Date(entry.shift_start_time),
        shift_end_time: new Date(entry.shift_end_time),
        work_shift_id: contextFields.workShift,
        isPlaceholder: false,
    }))

    const employeeEntries: iTemplateEmployeeEntryRow[] = applyPayload.employeeEntries.map(extra => ({
        employee: extra.employee,
        schema: schemasById[extra.schema] ?? extra.schema,
        project: projectsById[extra.project] ?? extra.project,
        store: extra.store,
        date: extra.date,
        foreman: contextFields.foreman,
        shift_start_time: new Date(extra.shift_start_time),
        shift_end_time: new Date(extra.shift_end_time),
        work_shift_id: contextFields.workShift,
        isPlaceholder: false,
    }))

    const absences: iTemplateAbsenceRow[] = applyPayload.absences.map(absence => ({
        employee: absence.employee,
        absence_type: absence.absence_type,
        type: absence.type,
        project: absence.project === null ? null : projectsById[absence.project] ?? absence.project,
        date: absence.date,
        foreman: contextFields.foreman,
        shift_start_time: new Date(absence.shift_start_time),
        shift_end_time: new Date(absence.shift_end_time),
        work_shift_id: contextFields.workShift,
        isPlaceholder: false,
    }))

    return { timekeepingEntries, employeeEntries, absences }
}

/**
 * Fetch the current company's timecard templates once, behind the WA-9280-timecard-templates
 * flag, for any modal that offers a template picker or a "Save as Template" action.
 *
 * @returns A `[templates, setTemplates]` pair, like `useState`'s, so callers can append/replace
 *     entries locally after a save/update without refetching.
 */
export const useTimecardTemplates = (): [iTimecardTemplate[], Dispatch<SetStateAction<iTimecardTemplate[]>>] => {
    const [templates, setTemplates] = useState<iTimecardTemplate[]>([])

    useEffect(() => {
        if (!getFlagEnabled("WA-9280-timecard-templates")) return
        API.getTimecardTemplates().then(setTemplates)
    }, [])

    return [templates, setTemplates]
}

/**
 * Drop template-applied rows that reference an employee, cost code, or project no longer present
 * in the create-time-card modal's current selections -- e.g. because the user removed one after
 * picking a full-matrix template, but before confirming the save.
 *
 * @param templateRows - The rows built by `buildTemplateRows` from a template apply response.
 * @param employees - The modal's currently selected employees.
 * @param costCodes - The modal's currently selected cost codes.
 * @param projects - The modal's currently selected projects.
 * @returns The same row shape, filtered down to rows that still fit the current selections.
 */
export const filterTemplateRowsBySelectors = (
    templateRows: ReturnType<typeof buildTemplateRows>,
    employees: Array<{ id: number }>,
    costCodes: Array<{ id: number }>,
    projects: Array<{ id: number }>
): ReturnType<typeof buildTemplateRows> => {
    const employeeIds = new Set(employees.map(employee => employee.id))
    const costCodeIds = new Set(costCodes.map(costCode => costCode.id))
    const projectIds = new Set(projects.map(project => project.id))
    const resolvedProjectId = (project: tProject | number | null): number | null => (project === null ? null : typeof project === "number" ? project : project.id ?? null)

    return {
        timekeepingEntries: templateRows.timekeepingEntries.filter(row => employeeIds.has(row.employee) && costCodeIds.has(row.cost_code)),
        employeeEntries: templateRows.employeeEntries.filter(row => {
            const projectId = resolvedProjectId(row.project)
            return employeeIds.has(row.employee) && projectId !== null && projectIds.has(projectId)
        }),
        absences: templateRows.absences.filter(row => {
            if (!employeeIds.has(row.employee)) return false
            const projectId = resolvedProjectId(row.project)
            return projectId === null || projectIds.has(projectId)
        }),
    }
}
